import { useUser } from "@clerk/expo";
import * as ImagePicker from "expo-image-picker";
import {
  CheckSquare,
  CircleDot,
  Gauge,
  Grid3X3,
  GripHorizontal,
  ListOrdered,
  MessageSquare,
  Shuffle,
  SlidersHorizontal,
  Star,
  Tag,
} from "lucide-react-native";
import { ComponentType, FC, useCallback, useEffect, useState } from "react";
import { Platform, Pressable, ScrollView } from "react-native";

import {
  Box,
  Button,
  ButtonText,
  Card,
  HStack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Question, QuestionDraft, VariableDef } from "@/types/resources";
import { QuestionType } from "@/types/utils";
import {
  CategoryAssignmentQuestionEditor,
  LinearScaleQuestionEditor,
  MultipleChoiceQuestionEditor,
  MultiSelectMatrixQuestionEditor,
  NpsQuestionEditor,
  OpenEndedQuestionEditor,
  PricingQuestionEditor,
  RankingQuestionEditor,
  RatingScaleQuestionEditor,
  SingleChoiceQuestionEditor,
  SingleSelectMatrixQuestionEditor,
} from "./editors";
import { QuestionVariablesProvider } from "./editors/QuestionVariablesContext";

const getDefaultOptions = (type: QuestionType): string[] => {
  switch (type) {
    case "linear_scale":
      return ["", "2", "3", "4", ""];
    case "multi_select_matrix":
    case "single_select_matrix":
      return ["", ""];
    case "ranking":
      return ["1", "2"];
    case "multiple_choice":
      return ["", ""];
    case "nps":
    case "open_ended":
    case "pricing":
      return [];
    case "rating_scale":
      return ["1", "2", "3", "4", "5"];
    case "category_assignment":
      return ["Track 1", "Track 2"];
    case "single_choice":
    default:
      return ["", ""];
  }
};

const getDefaultLines = (type: QuestionType): string[] => {
  switch (type) {
    case "multi_select_matrix":
    case "ranking":
    case "single_select_matrix":
      return ["", ""];
    default:
      return [];
  }
};

interface CreateQuestionProps {
  /** The study's existing questions — lets the create form offer carry-forward
      from any prior question (skip rules stay edit-only). */
  allQuestions?: Question[];
  onCancel?: () => void;
  onCreated?: (question: Question) => void;
  /** 1-based slot the new question will occupy in the study's flat list.
      The parent computes this from the gap the user clicked into; the
      backend trusts the (order, section_id) pair and shifts existing
      questions accordingly. */
  order: number;
  sectionId?: string;
  studyId: string;
  type?: QuestionType;
  types?: QuestionType[];
  /** Section variables exposed to the question text editor for `{{…}}`
      highlighting and autocomplete. */
  variables?: VariableDef[];
}

const CreateQuestion: FC<CreateQuestionProps> = ({
  allQuestions = [],
  onCancel,
  onCreated,
  order,
  sectionId,
  studyId,
  type: typeProp,
  types: allowedTypes,
  variables = [],
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;
  const { createQuestion, error, loading, uploadProgress } =
    API.Studies.useCreateQuestion(studyId);

  const initialType = typeProp || "single_choice";
  const [question, setQuestion] = useState<QuestionDraft<QuestionType>>({
    lines: getDefaultLines(initialType),
    media: [],
    options: getDefaultOptions(initialType),
    text: "",
    type: initialType,
  });
  const [useImages, setUseImages] = useState<boolean>(false);

  // Sync type from prop during render (not in an effect) when it changes. The
  // initial type is already seeded into the question state above.
  const [prevTypeProp, setPrevTypeProp] = useState(typeProp);
  if (prevTypeProp !== typeProp) {
    setPrevTypeProp(typeProp);
    if (typeProp) {
      setQuestion((prev) => ({
        ...prev,
        type: typeProp,
      }));
    }
  }

  const handleQuestionTypeChange = (type: QuestionType) => {
    setQuestion((prev) => ({
      ...prev,
      // Carry-forward / assignment are type-specific — drop them on a type
      // switch so a stale config isn't posted for a type that can't use it.
      assignment: null,
      carry_forward: null,
      lines: getDefaultLines(type),
      options: getDefaultOptions(type),
      type,
    }));
  };

  const isCreateDisabled = useCallback(() => {
    const MIN_OPTIONS = 2;
    const MIN_OPTIONS_SCALE = 3;
    const MIN_LINES = 2;

    switch (question.type) {
      case "category_assignment": {
        // Assignment is mandatory (the editor materializes a default on mount);
        // guard on it so a create can't slip through with `assignment: null`
        // before that default is committed. Track destinations aren't required
        // to create — a newly inserted assignment question is flagged in red
        // (see `questionHasError`) until routed, so authors can add downstream
        // questions first. (Create never sends rules anyway.)
        return (
          !question.assignment ||
          question.options.length < MIN_OPTIONS ||
          question.options.some((o) => !o.trim())
        );
      }
      case "single_choice":
        return (
          !question.text?.trim() ||
          (useImages
            ? question.media.length < MIN_OPTIONS
            : question.options.length < MIN_OPTIONS ||
              question.options.some((o) => !o.trim()))
        );
      case "multiple_choice":
        return (
          !question.text?.trim() ||
          (useImages
            ? question.media.length < MIN_OPTIONS
            : question.options.length < MIN_OPTIONS ||
              question.options.some((o) => !o.trim()))
        );
      case "ranking":
        return (
          !question.text?.trim() ||
          question.lines.length < MIN_LINES ||
          question.lines.some((l) => !l.trim())
        );
      case "single_select_matrix":
      case "multi_select_matrix":
        return (
          !question.text?.trim() ||
          question.options.length < MIN_OPTIONS ||
          question.options.some((o) => !o.trim()) ||
          question.lines.length < MIN_LINES ||
          question.lines.some((l) => !l.trim())
        );
      case "linear_scale":
        return (
          !question.text?.trim() ||
          question.options.length < MIN_OPTIONS_SCALE ||
          !question.options[0]?.trim() ||
          !question.options[question.options.length - 1]?.trim()
        );
      case "rating_scale":
        return (
          !question.text?.trim() || question.options.length < MIN_OPTIONS_SCALE
        );
      case "nps":
      case "open_ended":
      case "pricing":
        return !question.text?.trim();
      default:
        return true;
    }
  }, [question, useImages]);

  const handleSaveQuestion = async () => {
    const usesImages =
      useImages && ["single_choice", "multiple_choice"].includes(question.type);
    /* nps, open_ended and pricing all answer in free-form (numeric or text)
       — no authored options to ship. */
    const usesOptions = !["nps", "open_ended", "pricing"].includes(
      question.type
    );
    const usesLines = [
      "single_select_matrix",
      "multi_select_matrix",
      "ranking",
    ].includes(question.type);

    const result = await createQuestion({
      assignment: question.assignment ?? null,
      carry_forward: question.carry_forward ?? null,
      lines: usesLines ? question.lines : [],
      media: question.media as ImagePicker.ImagePickerAsset[],
      options: usesOptions && !usesImages ? question.options : [],
      order,
      section_id: sectionId,
      text: question.text,
      type: question.type,
    });
    if (result && onCreated) {
      onCreated(result);
    }
  };

  // Submit on Enter key
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && !isCreateDisabled() && !loading) {
        e.preventDefault();
        handleSaveQuestion();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const allQuestionTypeOptions: {
    icon: ComponentType<{ className?: string; size?: number }>;
    label: string;
    value: QuestionType;
  }[] = [
    {
      icon: CircleDot,
      label: i18n.t("questions.types.singleChoice"),
      value: "single_choice",
    },
    {
      icon: CheckSquare,
      label: i18n.t("questions.types.multipleChoice"),
      value: "multiple_choice",
    },
    {
      icon: MessageSquare,
      label: i18n.t("questions.types.openEnded"),
      value: "open_ended",
    },
    {
      icon: Star,
      label: i18n.t("questions.types.ratingScale"),
      value: "rating_scale",
    },
    {
      icon: SlidersHorizontal,
      label: i18n.t("questions.types.linearScale"),
      value: "linear_scale",
    },
    {
      icon: Gauge,
      label: i18n.t("questions.types.nps"),
      value: "nps",
    },
    {
      icon: Tag,
      label: i18n.t("questions.types.pricing"),
      value: "pricing",
    },
    {
      icon: ListOrdered,
      label: i18n.t("questions.types.ranking"),
      value: "ranking",
    },
    {
      icon: GripHorizontal,
      label: i18n.t("questions.types.singleSelectMatrix"),
      value: "single_select_matrix",
    },
    {
      icon: Grid3X3,
      label: i18n.t("questions.types.multiSelectMatrix"),
      value: "multi_select_matrix",
    },
    {
      icon: Shuffle,
      label: i18n.t("questions.types.categoryAssignment"),
      value: "category_assignment",
    },
  ];

  const questionTypeOptions = allQuestionTypeOptions.filter((opt) => {
    if (allowedTypes && !allowedTypes.includes(opt.value)) return false;
    if (!isAdmin && opt.value === "category_assignment") return false;
    return true;
  });

  return (
    <Card className="border border-outline-200 rounded-2xl p-6">
      <VStack className="gap-6">
        {/* Question Type Selection - Only show when type prop is not provided */}
        {!typeProp && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <HStack className="gap-2">
              {questionTypeOptions.map((option) => (
                <Pressable
                  key={option.value}
                  className={`w-40 px-3 py-4 rounded-lg border ${
                    question.type === option.value
                      ? "bg-primary-500 border-primary-500"
                      : "bg-white border-outline-200"
                  }`}
                  onPress={() => handleQuestionTypeChange(option.value)}
                >
                  <VStack className="items-center justify-between gap-2 h-full">
                    <option.icon
                      className={`${
                        question.type === option.value
                          ? "text-white"
                          : "text-typography-600"
                      }`}
                      size={20}
                    />
                    <Text
                      className={`text-xs font-medium text-center ${
                        question.type === option.value
                          ? "text-white"
                          : "text-typography-700"
                      }`}
                    >
                      {option.label}
                    </Text>
                  </VStack>
                </Pressable>
              ))}
            </HStack>
          </ScrollView>
        )}

        {/* Question Editor */}
        <QuestionVariablesProvider value={variables}>
          <Box>
            {question.type === "single_choice" && (
              <SingleChoiceQuestionEditor
                allQuestions={allQuestions}
                canEditRules={false}
                currentQuestionOrder={order}
                onChange={setQuestion}
                question={question as QuestionDraft<"single_choice">}
                setUseImages={setUseImages}
                useImages={useImages}
              />
            )}

            {question.type === "category_assignment" && (
              <CategoryAssignmentQuestionEditor
                allQuestions={allQuestions}
                currentQuestionOrder={order}
                onChange={setQuestion}
                question={question as QuestionDraft<"category_assignment">}
              />
            )}

            {question.type === "multiple_choice" && (
              <MultipleChoiceQuestionEditor
                allQuestions={allQuestions}
                canEditRules={false}
                currentQuestionOrder={order}
                onChange={setQuestion}
                question={question as QuestionDraft<"multiple_choice">}
                setUseImages={setUseImages}
                useImages={useImages}
              />
            )}

            {question.type === "open_ended" && (
              <OpenEndedQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"open_ended">}
              />
            )}

            {question.type === "nps" && (
              <NpsQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"nps">}
              />
            )}

            {question.type === "pricing" && (
              <PricingQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"pricing">}
              />
            )}

            {question.type === "rating_scale" && (
              <RatingScaleQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"rating_scale">}
              />
            )}

            {question.type === "linear_scale" && (
              <LinearScaleQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"linear_scale">}
              />
            )}

            {question.type === "single_select_matrix" && (
              <SingleSelectMatrixQuestionEditor
                allQuestions={allQuestions}
                currentQuestionOrder={order}
                onChange={setQuestion}
                question={question as QuestionDraft<"single_select_matrix">}
              />
            )}

            {question.type === "multi_select_matrix" && (
              <MultiSelectMatrixQuestionEditor
                allQuestions={allQuestions}
                currentQuestionOrder={order}
                onChange={setQuestion}
                question={question as QuestionDraft<"multi_select_matrix">}
              />
            )}

            {question.type === "ranking" && (
              <RankingQuestionEditor
                onChange={setQuestion}
                question={question as QuestionDraft<"ranking">}
              />
            )}
          </Box>
        </QuestionVariablesProvider>

        {/* Action Buttons */}
        <VStack className="gap-1 items-end">
          <HStack className="gap-4">
            {onCancel && (
              <Button
                className="px-6 py-3 rounded-lg"
                isDisabled={loading}
                onPress={onCancel}
                variant="outline"
              >
                <ButtonText className="text-typography-700">
                  {i18n.t("misc.cancel")}
                </ButtonText>
              </Button>
            )}
            <Button
              className="px-6 py-3 rounded-lg bg-primary-500"
              isDisabled={isCreateDisabled() || loading}
              onPress={handleSaveQuestion}
            >
              <ButtonText className="text-white">
                {uploadProgress > 0
                  ? i18n.t("misc.uploadingMedia", {
                      count: question.media.length,
                      percent: i18n.numberToPercentage(uploadProgress * 100, {
                        precision: 0,
                      }),
                    })
                  : loading
                    ? i18n.t("misc.creating")
                    : i18n.t("questions.createQuestion")}
              </ButtonText>
            </Button>
          </HStack>
          {error && <Text className="text-xs text-error-500">{error}</Text>}
        </VStack>
      </VStack>
    </Card>
  );
};

export default CreateQuestion;
